/**
 * Read-side queries over a State (and, where history is needed, the entries).
 * Everything here is a pure function; nothing is cached or stored.
 */
import {
  add,
  averageUnitCost,
  mulDivRound,
  neg,
  ZERO,
  ZERO_QTY,
  type Milli,
  type Paisa,
} from './money.js';
import {
  LEDGER,
  acctKey,
  isAcctKey,
  isPartyKey,
  itemState,
  keyId,
  ledger,
  partyKey,
  type State,
} from './project.js';
import { ACCOUNT_KIND_ORDER } from './types.js';
import type {
  Account,
  Entry,
  ISODate,
  Item,
  Master,
  Party,
  PartyType,
  SaleEntry,
} from './types.js';

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** Inclusive date range. */
export interface DateRange {
  readonly from: ISODate;
  readonly to: ISODate;
}

export const ALL_TIME: DateRange = { from: '0000-01-01', to: '9999-12-31' };

export const inRange = (date: ISODate, r: DateRange): boolean => date >= r.from && date <= r.to;

/** 'YYYY-MM-DD' → 'YYYY-MM' */
export const monthOf = (date: ISODate): string => date.slice(0, 7);

export function monthRange(month: string): DateRange {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** The n months ending with the month of `today`, oldest first. */
export function lastMonths(today: ISODate, n: number): string[] {
  const end = monthOf(today);
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) out.push(addMonths(end, -i));
  return out;
}

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

export interface AccountRow {
  readonly account: Account;
  readonly balance: Paisa;
}

/** Every account in master order (cash first), including closed ones that still hold money. */
export function accountRows(state: State, master: Master): AccountRow[] {
  const rows: AccountRow[] = [];
  for (const account of master.accounts.values()) {
    const balance = ledger(state, acctKey(account.id));
    if (!account.active && balance === 0) continue;
    rows.push({ account, balance });
  }
  return rows.sort(
    (a, b) => ACCOUNT_KIND_ORDER[a.account.kind] - ACCOUNT_KIND_ORDER[b.account.kind],
  );
}

export function cashInHand(state: State, master: Master): Paisa {
  let sum = ZERO;
  for (const r of accountRows(state, master))
    if (r.account.kind === 'cash') sum = add(sum, r.balance);
  return sum;
}

/** Everything that is not in the cash box: bank accounts and mobile wallets together. */
export function bankTotal(state: State, master: Master): Paisa {
  let sum = ZERO;
  for (const r of accountRows(state, master))
    if (r.account.kind !== 'cash') sum = add(sum, r.balance);
  return sum;
}

// ---------------------------------------------------------------------------
// Parties
// ---------------------------------------------------------------------------

export type PartyStatus = 'due' | 'advance' | 'settled';

export interface PartyRow {
  readonly party: Party;
  /** Natural sign: positive = the customer owes you / you owe the vendor. Negative = an advance is held. */
  readonly balance: Paisa;
  readonly status: PartyStatus;
}

/** Ledger balance in the party's natural direction. */
export function partyBalance(state: State, party: Party): Paisa {
  const raw = ledger(state, partyKey(party.id));
  return party.type === 'customer' ? raw : neg(raw);
}

export function partyStatus(balance: Paisa): PartyStatus {
  return balance > 0 ? 'due' : balance < 0 ? 'advance' : 'settled';
}

export function partyRows(state: State, master: Master, type: PartyType): PartyRow[] {
  const rows: PartyRow[] = [];
  for (const party of master.parties.values()) {
    if (party.type !== type) continue;
    const balance = partyBalance(state, party);
    if (!party.active && balance === 0) continue;
    rows.push({ party, balance, status: partyStatus(balance) });
  }
  return rows.sort((a, b) => b.balance - a.balance || a.party.name.localeCompare(b.party.name));
}

/** Total customers owe you (advances are not netted off). */
export function receivableTotal(state: State, master: Master): Paisa {
  let sum = ZERO;
  for (const r of partyRows(state, master, 'customer'))
    if (r.balance > 0) sum = add(sum, r.balance);
  return sum;
}

/** Total you owe vendors (advances are not netted off). */
export function payableTotal(state: State, master: Master): Paisa {
  let sum = ZERO;
  for (const r of partyRows(state, master, 'vendor')) if (r.balance > 0) sum = add(sum, r.balance);
  return sum;
}

// ---------------------------------------------------------------------------
// Stock
// ---------------------------------------------------------------------------

export interface StockRow {
  readonly item: Item;
  readonly qty: Milli;
  readonly defQty: Milli;
  /** Weighted average cost per unit, for display. */
  readonly avgCost: Paisa;
  readonly value: Paisa;
  readonly low: boolean;
  readonly out: boolean;
}

export function stockRow(state: State, item: Item): StockRow {
  const st = itemState(state, item.id);
  const avgCost = st.qty > 0 ? averageUnitCost(st.value, st.qty) : st.lastUnitCost;
  return {
    item,
    qty: st.qty,
    defQty: st.defQty,
    avgCost,
    value: st.value,
    low: st.qty <= item.minQty,
    out: st.qty === 0,
  };
}

export function stockRows(state: State, master: Master, includeInactive = false): StockRow[] {
  const rows: StockRow[] = [];
  for (const item of master.items.values()) {
    if (!item.active && !includeInactive) continue;
    rows.push(stockRow(state, item));
  }
  return rows.sort(
    (a, b) =>
      a.item.category.localeCompare(b.item.category) || a.item.name.localeCompare(b.item.name),
  );
}

export function lowStock(state: State, master: Master): StockRow[] {
  return stockRows(state, master).filter((r) => r.low);
}

export const stockValue = (state: State): Paisa => ledger(state, LEDGER.STOCK);
export const defectiveValue = (state: State): Paisa => ledger(state, LEDGER.DEFECTIVE);

// ---------------------------------------------------------------------------
// Entry helpers
// ---------------------------------------------------------------------------

/** Non-void entries that have not been voided, in seq order. */
export function activeEntries(state: State, entries: Iterable<Entry>): Entry[] {
  const out: Entry[] = [];
  for (const e of entries) {
    if (e.type === 'void') continue;
    if (state.effects.get(e.id)?.voidedBy != null) continue;
    out.push(e);
  }
  return out;
}

/** Newest first. */
export function recentEntries(state: State, entries: Iterable<Entry>, n: number): Entry[] {
  return activeEntries(state, entries)
    .sort((a, b) => b.seq - a.seq)
    .slice(0, n);
}

/** Chronological: by business date, then by append order. */
export function byDate(a: Entry, b: Entry): number {
  return a.date < b.date ? -1 : a.date > b.date ? 1 : a.seq - b.seq;
}

function ledgerDelta(state: State, entry: Entry, key: string): Paisa {
  const eff = state.effects.get(entry.id);
  if (!eff) return ZERO;
  let sum = ZERO;
  for (const [k, v] of eff.ledgers) if (k === key) sum = add(sum, v);
  return sum;
}

// ---------------------------------------------------------------------------
// Party ledger
// ---------------------------------------------------------------------------

export interface LedgerRow {
  readonly entry: Entry;
  /** Change to the outstanding balance, natural sign. Zero when voided. */
  readonly delta: Paisa;
  readonly running: Paisa;
  readonly voided: boolean;
}

export function partyLedger(
  state: State,
  master: Master,
  entries: Iterable<Entry>,
  partyId: string,
): LedgerRow[] {
  const party = master.parties.get(partyId);
  if (!party) return [];
  const key = partyKey(partyId);
  const rows: LedgerRow[] = [];
  const touching: Entry[] = [];
  for (const e of entries) {
    if (e.type === 'void') continue;
    const eff = state.effects.get(e.id);
    if (!eff) continue;
    if (eff.ledgers.some(([k]) => k === key)) touching.push(e);
  }
  touching.sort(byDate);
  let running = ZERO;
  for (const e of touching) {
    const voided = state.effects.get(e.id)?.voidedBy != null;
    const raw = ledgerDelta(state, e, key);
    const delta = voided ? ZERO : party.type === 'customer' ? raw : neg(raw);
    running = add(running, delta);
    rows.push({ entry: e, delta, running, voided });
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Period report
// ---------------------------------------------------------------------------

export interface PeriodReport {
  readonly range: DateRange;
  readonly sales: Paisa;
  readonly salesCount: number;
  readonly salesReturns: Paisa;
  readonly cogs: Paisa;
  readonly grossProfit: Paisa;
  /** Whole percent of sales, null when there were no sales. */
  readonly marginPct: number | null;
  readonly expenses: Paisa;
  /** Stock write-offs and purchase-return losses, net of gains. Positive = loss. */
  readonly stockAdjustments: Paisa;
  readonly netProfit: Paisa;
  readonly purchases: Paisa;
  readonly purchaseCount: number;
  readonly drawings: Paisa;
}

export function periodReport(
  state: State,
  entries: Iterable<Entry>,
  range: DateRange,
): PeriodReport {
  let sales = ZERO;
  let salesCount = 0;
  let salesReturns = ZERO;
  let cogs = ZERO;
  let expenses = ZERO;
  let stockAdjustments = ZERO;
  let purchases = ZERO;
  let purchaseCount = 0;
  let drawings = ZERO;
  for (const e of activeEntries(state, entries)) {
    if (!inRange(e.date, range)) continue;
    const eff = state.effects.get(e.id);
    if (!eff) continue;
    for (const [k, v] of eff.ledgers) {
      switch (k) {
        case LEDGER.SALES:
          sales = add(sales, neg(v));
          break;
        case LEDGER.SALES_RETURNS:
          salesReturns = add(salesReturns, v);
          break;
        case LEDGER.COGS:
          cogs = add(cogs, v);
          break;
        case LEDGER.EXPENSE:
          expenses = add(expenses, v);
          break;
        case LEDGER.STOCK_ADJ:
          stockAdjustments = add(stockAdjustments, v);
          break;
        case LEDGER.DRAWING:
          drawings = add(drawings, v);
          break;
        default:
          break;
      }
    }
    if (e.type === 'sale') salesCount += 1;
    if (e.type === 'purchase') {
      purchases = add(purchases, e.total);
      purchaseCount += 1;
    }
  }
  const grossProfit = add(add(sales, neg(salesReturns)), neg(cogs));
  const netProfit = add(add(grossProfit, neg(expenses)), neg(stockAdjustments));
  let marginPct: number | null = null;
  if (sales > 0) {
    const pct = mulDivRound(Math.abs(grossProfit), 100, sales);
    marginPct = grossProfit < 0 ? -pct : pct;
  }
  return {
    range,
    sales,
    salesCount,
    salesReturns,
    cogs,
    grossProfit,
    marginPct,
    expenses,
    stockAdjustments,
    netProfit,
    purchases,
    purchaseCount,
    drawings,
  };
}

export interface MonthSales {
  readonly month: string;
  readonly sales: Paisa;
  readonly count: number;
}

export function monthlySales(
  state: State,
  entries: Iterable<Entry>,
  months: ReadonlyArray<string>,
): MonthSales[] {
  const acc = new Map<string, { sales: Paisa; count: number }>();
  for (const m of months) acc.set(m, { sales: ZERO, count: 0 });
  for (const e of activeEntries(state, entries)) {
    if (e.type !== 'sale') continue;
    const slot = acc.get(monthOf(e.date));
    if (!slot) continue;
    slot.sales = add(slot.sales, e.total);
    slot.count += 1;
  }
  return months.map((month) => ({ month, ...acc.get(month)! }));
}

export interface TopItem {
  readonly item: Item;
  readonly qty: Milli;
  readonly amount: Paisa;
}

export function topItems(
  state: State,
  master: Master,
  entries: Iterable<Entry>,
  range: DateRange,
  n = 6,
): TopItem[] {
  const acc = new Map<string, { qty: Milli; amount: Paisa }>();
  for (const e of activeEntries(state, entries)) {
    if (e.type !== 'sale' || !inRange(e.date, range)) continue;
    for (const line of e.lines) {
      const slot = acc.get(line.itemId) ?? { qty: ZERO_QTY, amount: ZERO };
      acc.set(line.itemId, {
        qty: (slot.qty + line.qty) as Milli,
        amount: add(slot.amount, line.amount),
      });
    }
  }
  const out: TopItem[] = [];
  for (const [itemId, v] of acc) {
    const item = master.items.get(itemId);
    if (item) out.push({ item, ...v });
  }
  return out
    .sort((a, b) => b.amount - a.amount || a.item.name.localeCompare(b.item.name))
    .slice(0, n);
}

export interface DaySales {
  readonly total: Paisa;
  readonly count: number;
}

export function salesOn(state: State, entries: Iterable<Entry>, date: ISODate): DaySales {
  let total = ZERO;
  let count = 0;
  for (const e of activeEntries(state, entries)) {
    if (e.type === 'sale' && e.date === date) {
      total = add(total, e.total);
      count += 1;
    }
  }
  return { total, count };
}

export function salesEntries(state: State, entries: Iterable<Entry>): SaleEntry[] {
  return activeEntries(state, entries).filter((e): e is SaleEntry => e.type === 'sale');
}

// ---------------------------------------------------------------------------
// Position and trial balance
// ---------------------------------------------------------------------------

export interface Position {
  readonly stockValue: Paisa;
  readonly defectiveValue: Paisa;
  readonly cash: Paisa;
  readonly bank: Paisa;
  readonly receivable: Paisa;
  readonly payable: Paisa;
  /** Advances customers have paid you (a liability). */
  readonly customerAdvances: Paisa;
  /** Advances you have paid vendors (an asset). */
  readonly vendorAdvances: Paisa;
  /** Assets minus liabilities. Equals capital + profit − drawings by construction. */
  readonly netWorth: Paisa;
}

export function position(state: State, master: Master): Position {
  let customerAdvances = ZERO;
  let vendorAdvances = ZERO;
  for (const r of partyRows(state, master, 'customer'))
    if (r.balance < 0) customerAdvances = add(customerAdvances, neg(r.balance));
  for (const r of partyRows(state, master, 'vendor'))
    if (r.balance < 0) vendorAdvances = add(vendorAdvances, neg(r.balance));
  let netWorth = ZERO;
  for (const [k, v] of state.ledgers) {
    if (isAcctKey(k) || isPartyKey(k) || k === LEDGER.STOCK || k === LEDGER.DEFECTIVE)
      netWorth = add(netWorth, v);
  }
  return {
    stockValue: stockValue(state),
    defectiveValue: defectiveValue(state),
    cash: cashInHand(state, master),
    bank: bankTotal(state, master),
    receivable: receivableTotal(state, master),
    payable: payableTotal(state, master),
    customerAdvances,
    vendorAdvances,
    netWorth,
  };
}

export interface TrialRow {
  readonly key: string;
  readonly balance: Paisa;
}

export function trialBalance(state: State): { rows: TrialRow[]; sum: Paisa; ok: boolean } {
  const rows: TrialRow[] = [];
  let sum = ZERO;
  for (const [key, balance] of state.ledgers) {
    rows.push({ key, balance });
    sum = add(sum, balance);
  }
  rows.sort((a, b) => a.key.localeCompare(b.key));
  return { rows, sum, ok: sum === 0 };
}

// ---------------------------------------------------------------------------
// Account statement (cash / bank ledger)
// ---------------------------------------------------------------------------

export interface AccountLedgerRow {
  readonly entry: Entry;
  /** Money in (+) or out (−) of this account. Zero when voided. */
  readonly delta: Paisa;
  readonly running: Paisa;
  readonly voided: boolean;
}

/** Chronological statement of one cash or bank account with a running balance. */
export function accountLedger(
  state: State,
  entries: Iterable<Entry>,
  accountId: string,
): AccountLedgerRow[] {
  const key = acctKey(accountId);
  const touching: Entry[] = [];
  for (const e of entries) {
    if (e.type === 'void') continue;
    const eff = state.effects.get(e.id);
    if (eff && eff.ledgers.some(([k]) => k === key)) touching.push(e);
  }
  touching.sort(byDate);
  let running = ZERO;
  const rows: AccountLedgerRow[] = [];
  for (const e of touching) {
    const voided = state.effects.get(e.id)?.voidedBy != null;
    const delta = voided ? ZERO : ledgerDelta(state, e, key);
    running = add(running, delta);
    rows.push({ entry: e, delta, running, voided });
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Cash flow
// ---------------------------------------------------------------------------

export interface CashFlowIn {
  readonly salesCash: Paisa;
  readonly customerPayments: Paisa;
  readonly vendorRefunds: Paisa;
  readonly openingBalances: Paisa;
  readonly total: Paisa;
}

export interface CashFlowOut {
  readonly purchasesCash: Paisa;
  readonly vendorPayments: Paisa;
  readonly customerRefunds: Paisa;
  readonly expenses: Paisa;
  readonly drawings: Paisa;
  readonly total: Paisa;
}

export interface CashFlowAccount {
  readonly account: Account;
  readonly opening: Paisa;
  readonly inflow: Paisa;
  readonly outflow: Paisa;
  readonly closing: Paisa;
}

export interface CashFlow {
  readonly range: DateRange;
  /** All cash and bank accounts together, the day before `range.from`. */
  readonly opening: Paisa;
  readonly inflows: CashFlowIn;
  readonly outflows: CashFlowOut;
  readonly net: Paisa;
  readonly closing: Paisa;
  readonly accounts: ReadonlyArray<CashFlowAccount>;
}

/** Net effect of one entry on all cash/bank accounts together (transfers net to zero). */
function cashDelta(state: State, entry: Entry): Paisa {
  const eff = state.effects.get(entry.id);
  if (!eff) return ZERO;
  let sum = ZERO;
  for (const [k, v] of eff.ledgers) if (isAcctKey(k)) sum = add(sum, v);
  return sum;
}

/**
 * Where the money came from and where it went in a period, by business date.
 * Built from each entry's actual postings, so it always reconciles: opening + net = closing,
 * and closing over ALL_TIME equals the sum of the account balances.
 */
export function cashFlow(
  state: State,
  master: Master,
  entries: Iterable<Entry>,
  range: DateRange,
): CashFlow {
  const active = activeEntries(state, entries);
  let opening = ZERO;
  let salesCash = ZERO;
  let customerPayments = ZERO;
  let vendorRefunds = ZERO;
  let openingBalances = ZERO;
  let purchasesCash = ZERO;
  let vendorPayments = ZERO;
  let customerRefunds = ZERO;
  let expenses = ZERO;
  let drawings = ZERO;
  const perAccount = new Map<string, { opening: Paisa; inflow: Paisa; outflow: Paisa }>();
  const slot = (id: string) => {
    let s = perAccount.get(id);
    if (!s) {
      s = { opening: ZERO, inflow: ZERO, outflow: ZERO };
      perAccount.set(id, s);
    }
    return s;
  };

  for (const e of active) {
    const eff = state.effects.get(e.id);
    if (!eff) continue;
    const before = e.date < range.from;
    const inside = inRange(e.date, range);
    if (!before && !inside) continue;
    for (const [k, v] of eff.ledgers) {
      if (!isAcctKey(k)) continue;
      const s = slot(keyId(k));
      if (before) s.opening = add(s.opening, v);
      else if (v > 0) s.inflow = add(s.inflow, v);
      else s.outflow = add(s.outflow, neg(v));
    }
    if (before) {
      opening = add(opening, cashDelta(state, e));
      continue;
    }
    const d = cashDelta(state, e);
    switch (e.type) {
      case 'sale':
        salesCash = add(salesCash, d);
        break;
      case 'payment':
        if (e.direction === 'in') customerPayments = add(customerPayments, d);
        else vendorPayments = add(vendorPayments, neg(d));
        break;
      case 'purchase':
        purchasesCash = add(purchasesCash, neg(d));
        break;
      case 'return':
        if (e.kind === 'sale') customerRefunds = add(customerRefunds, neg(d));
        else vendorRefunds = add(vendorRefunds, d);
        break;
      case 'expense':
        expenses = add(expenses, neg(d));
        break;
      case 'drawing':
        drawings = add(drawings, neg(d));
        break;
      case 'account_opening':
        openingBalances = add(openingBalances, d);
        break;
      default:
        break; // transfers net to zero; stock/party openings do not touch cash
    }
  }
  const inTotal = add(add(salesCash, customerPayments), add(vendorRefunds, openingBalances));
  const outTotal = add(
    add(add(purchasesCash, vendorPayments), add(customerRefunds, expenses)),
    drawings,
  );
  const net = add(inTotal, neg(outTotal));
  const accounts: CashFlowAccount[] = [];
  for (const account of master.accounts.values()) {
    const s = perAccount.get(account.id);
    if (!s && !account.active) continue;
    const opening_ = s?.opening ?? ZERO;
    const inflow = s?.inflow ?? ZERO;
    const outflow = s?.outflow ?? ZERO;
    accounts.push({
      account,
      opening: opening_,
      inflow,
      outflow,
      closing: add(add(opening_, inflow), neg(outflow)),
    });
  }
  accounts.sort((a, b) => ACCOUNT_KIND_ORDER[a.account.kind] - ACCOUNT_KIND_ORDER[b.account.kind]);
  return {
    range,
    opening,
    inflows: { salesCash, customerPayments, vendorRefunds, openingBalances, total: inTotal },
    outflows: {
      purchasesCash,
      vendorPayments,
      customerRefunds,
      expenses,
      drawings,
      total: outTotal,
    },
    net,
    closing: add(opening, net),
    accounts,
  };
}

// ---------------------------------------------------------------------------
// Trial balance (labelled) and balance sheet
// ---------------------------------------------------------------------------

export type TrialKind =
  | 'account'
  | 'party'
  | 'stock'
  | 'defective'
  | 'opening'
  | 'sales'
  | 'sales_returns'
  | 'cogs'
  | 'expense'
  | 'drawing'
  | 'stock_adj';

export interface TrialBalanceRow {
  readonly kind: TrialKind;
  /** Account or party id for kind 'account' / 'party', else the ledger key. */
  readonly id: string;
  readonly name: string;
  readonly debit: Paisa;
  readonly credit: Paisa;
}

export interface TrialBalanceReport {
  readonly rows: ReadonlyArray<TrialBalanceRow>;
  readonly totalDebit: Paisa;
  readonly totalCredit: Paisa;
  readonly balanced: boolean;
}

const KIND_ORDER: Record<TrialKind, number> = {
  account: 0,
  stock: 1,
  defective: 2,
  party: 3,
  opening: 4,
  sales: 5,
  sales_returns: 6,
  cogs: 7,
  expense: 8,
  drawing: 9,
  stock_adj: 10,
};

export function trialBalanceReport(state: State, master: Master): TrialBalanceReport {
  const rows: TrialBalanceRow[] = [];
  let totalDebit = ZERO;
  let totalCredit = ZERO;
  for (const [key, balance] of state.ledgers) {
    if (balance === 0) continue;
    let kind: TrialKind;
    let id = key;
    let name = key;
    if (isAcctKey(key)) {
      kind = 'account';
      id = keyId(key);
      name = master.accounts.get(id)?.name ?? id;
    } else if (isPartyKey(key)) {
      kind = 'party';
      id = keyId(key);
      name = master.parties.get(id)?.name ?? id;
    } else {
      kind = key as TrialKind;
    }
    const debit = balance > 0 ? balance : ZERO;
    const credit = balance < 0 ? neg(balance) : ZERO;
    totalDebit = add(totalDebit, debit);
    totalCredit = add(totalCredit, credit);
    rows.push({ kind, id, name, debit, credit });
  }
  const acctRank = (r: TrialBalanceRow): number =>
    r.kind === 'account' ? ACCOUNT_KIND_ORDER[master.accounts.get(r.id)?.kind ?? 'mobile'] : 0;
  rows.sort(
    (a, b) =>
      KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
      acctRank(a) - acctRank(b) ||
      a.name.localeCompare(b.name),
  );
  return { rows, totalDebit, totalCredit, balanced: totalDebit === totalCredit };
}

export interface BalanceLine {
  readonly kind:
    | 'account'
    | 'stock'
    | 'defective'
    | 'receivable'
    | 'vendor_advance'
    | 'payable'
    | 'customer_advance'
    | 'overdraft'
    | 'capital'
    | 'profit'
    | 'drawings';
  /** Account or party id when the line is one row of master data, else ''. */
  readonly id: string;
  readonly name: string;
  readonly amount: Paisa;
}

export interface BalanceSheet {
  readonly assets: ReadonlyArray<BalanceLine>;
  readonly liabilities: ReadonlyArray<BalanceLine>;
  readonly equity: ReadonlyArray<BalanceLine>;
  readonly totalAssets: Paisa;
  readonly totalLiabilities: Paisa;
  readonly totalEquity: Paisa;
  /** Assets = liabilities + equity. True whenever the books are consistent. */
  readonly balanced: boolean;
}

/** Balance sheet as of now. Party balances are shown per party; advances land on the other side. */
export function balanceSheet(state: State, master: Master): BalanceSheet {
  const assets: BalanceLine[] = [];
  const liabilities: BalanceLine[] = [];
  const equity: BalanceLine[] = [];
  for (const r of accountRows(state, master)) {
    if (r.balance >= 0)
      assets.push({ kind: 'account', id: r.account.id, name: r.account.name, amount: r.balance });
    else
      liabilities.push({
        kind: 'overdraft',
        id: r.account.id,
        name: r.account.name,
        amount: neg(r.balance),
      });
  }
  const stock = stockValue(state);
  if (stock !== 0) assets.push({ kind: 'stock', id: '', name: 'stock', amount: stock });
  const def = defectiveValue(state);
  if (def !== 0) assets.push({ kind: 'defective', id: '', name: 'defective', amount: def });
  for (const r of partyRows(state, master, 'customer')) {
    if (r.balance > 0)
      assets.push({ kind: 'receivable', id: r.party.id, name: r.party.name, amount: r.balance });
    else if (r.balance < 0)
      liabilities.push({
        kind: 'customer_advance',
        id: r.party.id,
        name: r.party.name,
        amount: neg(r.balance),
      });
  }
  for (const r of partyRows(state, master, 'vendor')) {
    if (r.balance > 0)
      liabilities.push({ kind: 'payable', id: r.party.id, name: r.party.name, amount: r.balance });
    else if (r.balance < 0)
      assets.push({
        kind: 'vendor_advance',
        id: r.party.id,
        name: r.party.name,
        amount: neg(r.balance),
      });
  }
  const capital = neg(ledger(state, LEDGER.OPENING));
  const profit = neg(
    add(
      add(ledger(state, LEDGER.SALES), ledger(state, LEDGER.SALES_RETURNS)),
      add(
        add(ledger(state, LEDGER.COGS), ledger(state, LEDGER.EXPENSE)),
        ledger(state, LEDGER.STOCK_ADJ),
      ),
    ),
  );
  const drawings = ledger(state, LEDGER.DRAWING);
  equity.push({ kind: 'capital', id: '', name: 'capital', amount: capital });
  equity.push({ kind: 'profit', id: '', name: 'profit', amount: profit });
  if (drawings !== 0)
    equity.push({ kind: 'drawings', id: '', name: 'drawings', amount: neg(drawings) });
  const sum = (lines: BalanceLine[]) => lines.reduce((t, l) => add(t, l.amount), ZERO);
  const totalAssets = sum(assets);
  const totalLiabilities = sum(liabilities);
  const totalEquity = sum(equity);
  return {
    assets,
    liabilities,
    equity,
    totalAssets,
    totalLiabilities,
    totalEquity,
    balanced: totalAssets === add(totalLiabilities, totalEquity),
  };
}

// ---------------------------------------------------------------------------
// Slow and dead stock
// ---------------------------------------------------------------------------

export interface SlowStockRow {
  readonly item: Item;
  readonly qty: Milli;
  /** Value tied up, at cost. */
  readonly value: Paisa;
  /** Last non-voided sale that included the item, or null if never sold. */
  readonly lastSale: ISODate | null;
  readonly daysSince: number | null;
}

export interface SlowStockReport {
  readonly days: number;
  readonly rows: ReadonlyArray<SlowStockRow>;
  readonly count: number;
  readonly value: Paisa;
  readonly neverSold: number;
}

function daysBetween(from: ISODate, to: ISODate): number {
  return Math.round((Date.parse(to + 'T00:00:00Z') - Date.parse(from + 'T00:00:00Z')) / 86_400_000);
}

/** Items still in stock that have not sold for `days` days (or ever), biggest value first. */
export function slowStock(
  state: State,
  master: Master,
  entries: Iterable<Entry>,
  today: ISODate,
  days = 180,
): SlowStockReport {
  const last = new Map<string, ISODate>();
  for (const e of activeEntries(state, entries)) {
    if (e.type !== 'sale') continue;
    for (const l of e.lines) {
      const prev = last.get(l.itemId);
      if (!prev || prev < e.date) last.set(l.itemId, e.date);
    }
  }
  const rows: SlowStockRow[] = [];
  let value = ZERO;
  let neverSold = 0;
  for (const item of master.items.values()) {
    if (!item.active) continue;
    const st = itemState(state, item.id);
    if (st.qty <= 0) continue;
    const lastSale = last.get(item.id) ?? null;
    const daysSince = lastSale ? daysBetween(lastSale, today) : null;
    if (daysSince !== null && daysSince < days) continue;
    if (lastSale === null) neverSold += 1;
    value = add(value, st.value);
    rows.push({ item, qty: st.qty, value: st.value, lastSale, daysSince });
  }
  rows.sort((a, b) => b.value - a.value || a.item.name.localeCompare(b.item.name));
  return { days, rows, count: rows.length, value, neverSold };
}
